"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  ArrowRight,
  Loader2,
  TrendingUp,
  FileText,
  AlertTriangle,
  Shield,
  BarChart3,
  XCircle,
  Users,
  Activity,
} from "lucide-react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { formatCurrency } from "../../lib/textron-data-processor";
import { getCompanyName } from "../../lib/textron-data-processor";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface EqrDashboardProps {
  setActiveSection?: (section: string) => void;
}

interface ScopingSummary {
  exceedsMateriality: number;
  consideredSignificant: number;
  qualitativelyMaterial: number;
  qualitativelyMaterialNotExceeding: number;
}

interface MaterialityTableData {
  entity: {
    planningMateriality: number;
    revisedMateriality: number | null;
    performanceMateriality: number;
    ctt: number;
  };
  abcotds: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string;
    performanceMateriality: number;
    ctt: string;
  }>;
  components: Array<{
    name: string;
    planningMateriality: number;
    revisedMateriality: string;
    performanceMateriality: number;
    ctt: number;
  }>;
}

interface Deficiency {
  id: string;
  deficiencyType: string;
  description: string | null;
}

interface Misstatement {
  id: string;
  type: string;
  workspace: string;
  amount: number;
}

interface SignificantRisk {
  rommId: string;
  workspace: string;
  description: string;
  controlIds: string[];
  procedureIds: string[];
}

interface IndependenceSummary {
  totalPeople: number;
  declaredIndependence: number;
  yetToDeclare: number;
  addedNotDeclared: number;
  declaredNotAdded: number;
}

interface ExternalConfirmationsSummary {
  domainTesting: {
    total: number;
    active: number;
    notFound: number;
    generalDomain: number;
    others: number;
  };
  confirmations: {
    total: number;
    received: number;
    notReceived: number;
    withAlternateTesting: number;
    uniqueWorkspaces: number;
    uniqueSampleSets: number;
  };
  workspaceBreakdown: Array<{
    workspace: string;
    total: number;
    received: number;
    notReceived: number;
  }>;
}

export function EqrDashboard({ setActiveSection }: EqrDashboardProps = {}) {
  const { currentEngagement } = useEngagement();
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState<string>("");
  const [scopingSummary, setScopingSummary] = useState<ScopingSummary>({
    exceedsMateriality: 0,
    consideredSignificant: 0,
    qualitativelyMaterial: 0,
    qualitativelyMaterialNotExceeding: 0,
  });
  const [materialityTableData, setMaterialityTableData] = useState<MaterialityTableData>({
    entity: {
      planningMateriality: 0,
      revisedMateriality: null,
      performanceMateriality: 0,
      ctt: 0,
    },
    abcotds: [],
    components: [],
  });
  const [materialityLoading, setMaterialityLoading] = useState(true);
  const [scopingLoading, setScopingLoading] = useState(true);
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [misstatements, setMisstatements] = useState<Misstatement[]>([]);
  const [significantRisks, setSignificantRisks] = useState<SignificantRisk[]>([]);
  const [risksLoading, setRisksLoading] = useState(true);
  const [deficienciesLoading, setDeficienciesLoading] = useState(true);
  const [misstatementsLoading, setMisstatementsLoading] = useState(true);
  const [rembEstimatesTable, setRembEstimatesTable] = useState<any[]>([]);
  const [estimatesLoading, setEstimatesLoading] = useState(true);
  const [materialityData, setMaterialityData] = useState({
    planningMateriality: 0,
    performanceMateriality: 0,
    clearlyTrivialThreshold: 0,
  });
  const [independenceSummary, setIndependenceSummary] = useState<IndependenceSummary>({
    totalPeople: 0,
    declaredIndependence: 0,
    yetToDeclare: 0,
    addedNotDeclared: 0,
    declaredNotAdded: 0,
  });
  const [engagementTeamLoading, setEngagementTeamLoading] = useState(true);
  const [externalConfirmationsLoading, setExternalConfirmationsLoading] = useState(true);
  const [externalConfirmationsSummary, setExternalConfirmationsSummary] = useState<ExternalConfirmationsSummary>({
    domainTesting: { total: 0, active: 0, notFound: 0, generalDomain: 0, others: 0 },
    confirmations: { total: 0, received: 0, notReceived: 0, withAlternateTesting: 0, uniqueWorkspaces: 0, uniqueSampleSets: 0 },
    workspaceBreakdown: [],
  });

  const loadPlanningResponsesFromFile = async (resolvedCompanyName: string) => {
    const result = await (window as any).sharePointAPI?.downloadClientJson?.("Planning_UserResponses.json");
    if (!(result?.success && result?.data)) return null;
    const dataArray = Array.isArray(result.data) ? result.data : [result.data];
    const sectionMap: Record<string, any> = {};
    for (const sectionData of dataArray) {
      if (!sectionData || typeof sectionData !== "object") continue;
      if (
        sectionData.companyName &&
        resolvedCompanyName &&
        sectionData.companyName !== resolvedCompanyName
      ) {
        continue;
      }
      const key = sectionData.sectionKey || sectionData.sectionName;
      if (key && sectionData.responses) sectionMap[String(key)] = sectionData.responses;
    }
    return sectionMap;
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load Materiality
  useEffect(() => {
    const loadMateriality = async () => {
      if (!companyName) return;
      setMaterialityLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("Planning_UserResponses.json");
          if (result?.success && result?.data) {
            const data = result.data;
            const dataArray = Array.isArray(data) ? data : [data];
            let materialityResponses: any = null;
            
            for (const sectionData of dataArray) {
              if (sectionData.sectionName === "Materiality" || 
                  sectionData.sectionKey === "materiality" ||
                  (sectionData.responses && (sectionData.responses["M-3"] || sectionData.responses["revisedMateriality"]))) {
                if (!sectionData.companyName || sectionData.companyName === companyName) {
                  materialityResponses = sectionData.responses || {};
                  break;
                }
              }
            }
            
            if (!materialityResponses) {
              const planningSections = await loadPlanningResponsesFromFile(companyName);
              materialityResponses =
                planningSections?.materiality ||
                planningSections?.Materiality ||
                null;
            }
            
            if (materialityResponses) {
              const m3Response = materialityResponses["M-3"];
              let planningMateriality = 0;
              if (m3Response?.textResponse) {
                const textValue = typeof m3Response.textResponse === "string"
                  ? m3Response.textResponse
                  : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
                planningMateriality = parseFloat(textValue) || 0;
              }

              const revisedMaterialityResponse = materialityResponses["revisedMateriality"];
              const revisedMateriality = revisedMaterialityResponse?.determinedMateriality > 0
                ? revisedMaterialityResponse.determinedMateriality
                : null;

              let performanceMateriality = 0;
              if (revisedMaterialityResponse?.performanceMateriality > 0) {
                performanceMateriality = revisedMaterialityResponse.performanceMateriality;
              } else {
                const m5Response = materialityResponses["M-5"];
                if (m5Response?.overridePerformanceMateriality) {
                  performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality) || 0;
                }
              }

              let ctt = 0;
              if (revisedMaterialityResponse?.trivialThreshold > 0) {
                ctt = revisedMaterialityResponse.trivialThreshold;
              } else {
                const m8Response = materialityResponses["M-8"];
                if (m8Response?.textResponse) {
                  const textValue = typeof m8Response.textResponse === "string"
                    ? m8Response.textResponse
                    : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
                  ctt = parseFloat(textValue) || 0;
                }
              }

              const abcotds: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: string;
              }> = [];
              
              const abcotdTable = materialityResponses["abcotdTable"];
              if (abcotdTable?.columns && Array.isArray(abcotdTable.columns)) {
                abcotdTable.columns.forEach((col: any) => {
                  if (col.values) {
                    abcotds.push({
                      name: col.values.abcotd || "",
                      planningMateriality: col.values.determinedMateriality || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: col.values.determinedPM || 0,
                      ctt: "NA",
                    });
                  }
                });
              }

              const components: Array<{
                name: string;
                planningMateriality: number;
                revisedMateriality: string;
                performanceMateriality: number;
                ctt: number;
              }> = [];
              
              const componentsData = materialityResponses["components"];
              if (componentsData && Array.isArray(componentsData)) {
                componentsData.forEach((comp: any) => {
                  if (comp.componentName && comp.table?.rows) {
                    const materialityRow = comp.table.rows.find((r: any) => r.particular === "Materiality");
                    const performanceRow = comp.table.rows.find((r: any) => r.particular === "Performance Materiality");
                    const cttRow = comp.table.rows.find((r: any) => r.particular === "Clearly Trivial Threshold");
                    
                    components.push({
                      name: comp.componentName || "",
                      planningMateriality: materialityRow?.determinedAmount || 0,
                      revisedMateriality: "NA",
                      performanceMateriality: performanceRow?.determinedAmount || 0,
                      ctt: cttRow?.determinedAmount || 0,
                    });
                  }
                });
              }

              setMaterialityTableData({
                entity: {
                  planningMateriality,
                  revisedMateriality,
                  performanceMateriality,
                  ctt,
                },
                abcotds,
                components,
              });

              // Set materialityData for misstatement summary
              setMaterialityData({
                planningMateriality,
                performanceMateriality,
                clearlyTrivialThreshold: ctt,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to load materiality:", error);
      } finally {
        setMaterialityLoading(false);
      }
    };
    loadMateriality();
  }, [companyName]);

  // Load Scoping Summary
  useEffect(() => {
    const loadScopingSummary = async () => {
      if (!companyName) return;
      setScopingLoading(true);
      try {
        const planningSections = await loadPlanningResponsesFromFile(companyName);
        const responses =
          planningSections?.["preliminary-analytical"] ||
          planningSections?.["Preliminary Analytical Procedures"] ||
          null;
        if (!responses) return;
        const balanceSheetItems = responses["PreliminaryAnalyticalProcedures_BalanceSheetItems"] || [];
        const profitLossItems = responses["PreliminaryAnalyticalProcedures_ProfitLossItems"] || [];
        const allItems = [...(Array.isArray(balanceSheetItems) ? balanceSheetItems : []), ...(Array.isArray(profitLossItems) ? profitLossItems : [])];

        let exceedsMateriality = 0;
        let consideredSignificant = 0;
        let qualitativelyMaterial = 0;
        let qualitativelyMaterialNotExceeding = 0;

        allItems.forEach((item: any) => {
          const exceeds = item.exceeds_materiality === true || item.exceeds_materiality === "Yes";
          const significant = item.considered_significant === "Significant";
          const qualMaterial = item.qualitatively_material === "Yes" || item.qualitatively_material === true;

          if (exceeds) {
            exceedsMateriality++;
          }
          if (significant) {
            consideredSignificant++;
          }
          if (qualMaterial && exceeds) {
            qualitativelyMaterial++;
          }
          if (qualMaterial && !exceeds) {
            qualitativelyMaterialNotExceeding++;
          }
        });

        setScopingSummary({
          exceedsMateriality,
          consideredSignificant,
          qualitativelyMaterial,
          qualitativelyMaterialNotExceeding,
        });
      } catch (error) {
        console.error("Error loading scoping summary:", error);
      } finally {
        setScopingLoading(false);
      }
    };
    loadScopingSummary();
  }, [companyName]);

  // Load Significant Risks
  useEffect(() => {
    const loadSignificantRisks = async () => {
      setRisksLoading(true);
      try {
        let romms: any[] = [];
        if ((window as any).sharePointAPI?.readRommLibrary) {
          const rommRes = await (window as any).sharePointAPI.readRommLibrary();
          if (rommRes.success && rommRes.data?.romm_library) {
            romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
          }
        }

        const significant = romms.filter((romm) => {
          const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
          return assessment.includes("significant");
        });

        const risks: SignificantRisk[] = significant.map((romm) => {
          const rommId = romm["romm-id"] || romm.romm_id || "";
          const workspace = Array.isArray(romm.workspace)
            ? romm.workspace[0] || ""
            : romm.workspace || "";
          const description = romm.description || "";
          const controlIds = Array.isArray(romm.control_id)
            ? romm.control_id.map(String)
            : romm.control_id
            ? [String(romm.control_id)]
            : [];
          const procedureIds = Array.isArray(romm.procedure_id)
            ? romm.procedure_id.map(String)
            : romm.procedure_id
            ? [String(romm.procedure_id)]
            : [];

          return {
            rommId,
            workspace,
            description,
            controlIds,
            procedureIds,
          };
        });

        setSignificantRisks(risks);
      } catch (error) {
        console.error("Error loading significant risks:", error);
      } finally {
        setRisksLoading(false);
      }
    };
    loadSignificantRisks();
  }, []);

  // Load Deficiencies
  useEffect(() => {
    const loadDeficiencies = async () => {
      setDeficienciesLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadDeficiencyJson) {
          const res = await (window as any).sharePointAPI.downloadDeficiencyJson();
          if (res.success && res.data) {
            const raw = res.data.libraries_deficiency || {};
            const items: Deficiency[] = Object.entries(raw).map(([id, value]: [string, any]) => ({
              id,
              deficiencyType: value.deficiency_type || "",
              description: value.description || null,
            }));
            setDeficiencies(items);
          }
        }
      } catch (error) {
        console.error("Error loading deficiencies:", error);
      } finally {
        setDeficienciesLoading(false);
      }
    };
    loadDeficiencies();
  }, []);

  // Load Misstatements
  useEffect(() => {
    const loadMisstatements = async () => {
      setMisstatementsLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadMisstatementJson) {
          const res = await (window as any).sharePointAPI.downloadMisstatementJson();
          if (res.success && res.data) {
            const librariesMisstatement = res.data.Libraries_Misstatement || res.data.libraries_Misstatement || {};
            const misstatementsObj = librariesMisstatement.misstatements || {};
            const items: Misstatement[] = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => {
              const type = value.type || "Factual";
              const typeKey = type.toLowerCase();
              const typeData = value[typeKey] || {};
              const misstatementAmount = typeData.misstatement || "0";
              
              return {
                id,
                type,
                workspace: value.workspace || "",
                amount: parseFloat(misstatementAmount) || 0,
              };
            });
            setMisstatements(items);
          }
        }
      } catch (error) {
        console.error("Error loading misstatements:", error);
      } finally {
        setMisstatementsLoading(false);
      }
    };
    loadMisstatements();
  }, []);

  // Load Summary of Management Estimates
  useEffect(() => {
    const loadManagementEstimates = async () => {
      setEstimatesLoading(true);
      try {
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          if (result?.success && result?.data) {
            const workspaceName = "Review of estimates for management bias";
            const remb1Data = result.data[workspaceName]?.["REMB-1"];
            if (remb1Data?.table && Array.isArray(remb1Data.table)) {
              setRembEstimatesTable(remb1Data.table);
            }
          }
        }
      } catch (error) {
        console.error("Error loading management estimates:", error);
      } finally {
        setEstimatesLoading(false);
      }
    };
    loadManagementEstimates();
  }, []);

  useEffect(() => {
    const allLoading = materialityLoading || scopingLoading || risksLoading || 
                       deficienciesLoading || misstatementsLoading || estimatesLoading ||
                       engagementTeamLoading || externalConfirmationsLoading;
    setLoading(allLoading);
  }, [materialityLoading, scopingLoading, risksLoading, deficienciesLoading, misstatementsLoading, estimatesLoading, engagementTeamLoading, externalConfirmationsLoading]);

  // Load Independence summary for Engagement Team section
  useEffect(() => {
    const loadIndependenceSummary = async () => {
      setEngagementTeamLoading(true);
      try {
        const [responseResult, teamResult] = await Promise.allSettled([
          (window as any).sharePointAPI?.downloadClientJson?.("Independence_Response.json"),
          currentEngagement ? apiService.getEngagementTeam(currentEngagement.id) : Promise.resolve([]),
        ]);

        const responseJson =
          responseResult.status === "fulfilled" &&
          responseResult.value?.success &&
          responseResult.value?.data &&
          typeof responseResult.value.data === "object" &&
          !Array.isArray(responseResult.value.data)
            ? (responseResult.value.data as Record<string, any>)
            : {};

        const teamMembers =
          teamResult.status === "fulfilled" && Array.isArray(teamResult.value)
            ? teamResult.value
            : [];

        const byId = new Map<string, { fromResponse?: any; fromTeam: boolean; name: string }>();

        Object.entries(responseJson).forEach(([key, entry]) => {
          if (!entry || typeof entry !== "object") return;
          const id = String(entry?.user?.id || key);
          const name = String(entry?.user?.name || key);
          byId.set(id, { fromResponse: entry, fromTeam: false, name });
        });

        teamMembers.forEach((member: any) => {
          const id = String(member?.id ?? member?.user_id ?? "");
          const name =
            String(
              member?.user_name ??
                [member?.first_name, member?.last_name].filter(Boolean).join(" ") ??
                member?.email ??
                ""
            ).trim() || id;
          const existing = byId.get(id);
          if (existing) {
            existing.fromTeam = true;
          } else {
            const byName = Array.from(byId.entries()).find(
              ([_, v]) => v.name.trim().toLowerCase() === name.toLowerCase()
            );
            if (byName) {
              byName[1].fromTeam = true;
            } else if (id) {
              byId.set(id, { fromTeam: true, name });
            }
          }
        });

        const mergedRows = Array.from(byId.values());
        const totalPeople = mergedRows.length;
        const declaredIndependence = mergedRows.filter(
          (r) => String(r.fromResponse?.submitted_at ?? "").trim() !== ""
        ).length;
        const yetToDeclare = totalPeople - declaredIndependence;

        const addedNotDeclared = mergedRows.filter((r) => {
          const added = r.fromTeam;
          const declared = String(r.fromResponse?.submitted_at ?? "").trim() !== "";
          return added && !declared;
        }).length;

        const declaredNotAdded = mergedRows.filter((r) => {
          const added = r.fromTeam;
          const declared = String(r.fromResponse?.submitted_at ?? "").trim() !== "";
          return declared && !added;
        }).length;

        setIndependenceSummary({
          totalPeople,
          declaredIndependence,
          yetToDeclare,
          addedNotDeclared,
          declaredNotAdded,
        });
      } catch (error) {
        console.error("Failed to load engagement team independence summary:", error);
        setIndependenceSummary({
          totalPeople: 0,
          declaredIndependence: 0,
          yetToDeclare: 0,
          addedNotDeclared: 0,
          declaredNotAdded: 0,
        });
      } finally {
        setEngagementTeamLoading(false);
      }
    };
    loadIndependenceSummary();
  }, [currentEngagement?.id]);

  // Load External Confirmations Summary from confirmation library sources
  useEffect(() => {
    const loadExternalConfirmationsSummary = async () => {
      if (!companyName) return;
      setExternalConfirmationsLoading(true);
      try {
        if (!(window as any).sharePointAPI?.downloadClientJson) {
          return;
        }

        const [domainRes, confirmationRes, activityRes] = await Promise.all([
          (window as any).sharePointAPI.downloadClientJson("Confirmation_DomainTesting.json"),
          (window as any).sharePointAPI.downloadClientJson("Confirmation_Response.json"),
          (window as any).sharePointAPI.downloadClientJson("Confirmation_ActivityLog.json"),
        ]);

        const domainData = domainRes?.success ? domainRes?.data : null;
        const domainRecords = Array.isArray(domainData?.domain_records)
          ? domainData.domain_records
          : Array.isArray(domainData)
            ? domainData
            : (domainData?.domain_info && domainData?.confirming_party_info)
              ? [domainData]
              : [];

        let domainActive = 0;
        let domainNotFound = 0;
        let domainGeneral = 0;
        let domainOthers = 0;

        domainRecords.forEach((record: any) => {
          const status = String(record?.domain_info?.status || "").trim().toLowerCase();
          if (status === "active") {
            domainActive += 1;
          } else if (status === "not found" || status === "notfound") {
            domainNotFound += 1;
          } else if (status === "general domain" || status === "generaldomain") {
            domainGeneral += 1;
          } else if (status) {
            domainOthers += 1;
          }
        });

        const confirmationsData = confirmationRes?.success && confirmationRes?.data && typeof confirmationRes.data === "object"
          ? confirmationRes.data
          : {};
        const timeline = Array.isArray(activityRes?.data?.timeline) ? activityRes.data.timeline : [];

        const receivedConfirmationIds = new Set<string>();
        timeline.forEach((entry: any) => {
          const letterId = String(entry?.letter_id ?? "").trim();
          const action = String(entry?.action ?? "").trim().toLowerCase();
          if (letterId && action === "confirmation received") {
            receivedConfirmationIds.add(letterId);
          }
        });

        const workspaceMap = new Map<string, { total: number; received: number; notReceived: number }>();
        const workspaceSet = new Set<string>();
        const sampleSetSet = new Set<string>();
        let totalConfirmations = 0;
        let receivedCount = 0;
        let alternateTestingCount = 0;

        Object.entries(confirmationsData as Record<string, any>).forEach(([confirmationId, confirmationValue]) => {
          totalConfirmations += 1;
          const partydetails = confirmationValue?.partydetails || {};
          const workspace = String(partydetails?.area || "").trim();
          const sampleSet = String(partydetails?.sample_set || "").trim();
          const isReceived = receivedConfirmationIds.has(String(confirmationId).trim());

          if (isReceived) receivedCount += 1;
          if (confirmationValue?.alternateTesting?.fileName) alternateTestingCount += 1;
          if (workspace) workspaceSet.add(workspace);
          if (sampleSet) sampleSetSet.add(sampleSet);

          if (workspace) {
            const existing = workspaceMap.get(workspace) || { total: 0, received: 0, notReceived: 0 };
            existing.total += 1;
            if (isReceived) {
              existing.received += 1;
            } else {
              existing.notReceived += 1;
            }
            workspaceMap.set(workspace, existing);
          }
        });

        const workspaceBreakdown = Array.from(workspaceMap.entries())
          .map(([workspace, values]) => ({
            workspace,
            total: values.total,
            received: values.received,
            notReceived: values.notReceived,
          }))
          .sort((a, b) => b.total - a.total || a.workspace.localeCompare(b.workspace));

        setExternalConfirmationsSummary({
          domainTesting: {
            total: domainRecords.length,
            active: domainActive,
            notFound: domainNotFound,
            generalDomain: domainGeneral,
            others: domainOthers,
          },
          confirmations: {
            total: totalConfirmations,
            received: receivedCount,
            notReceived: Math.max(totalConfirmations - receivedCount, 0),
            withAlternateTesting: alternateTestingCount,
            uniqueWorkspaces: workspaceSet.size,
            uniqueSampleSets: sampleSetSet.size,
          },
          workspaceBreakdown,
        });
      } catch (error) {
        console.error("Failed to load external confirmations summary:", error);
      } finally {
        setExternalConfirmationsLoading(false);
      }
    };
    loadExternalConfirmationsSummary();
  }, [companyName]);

  const totalMisstatementAmount = misstatements.reduce((sum, mis) => sum + Math.abs(mis.amount), 0);

  if (loading && !companyName) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          <span className="text-white">Loading EQR dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">EQR Dashboard</h1>
            <p className="text-white/60 mt-1">Engagement Quality Review Dashboard</p>
          </div>
        </div>

        {/* 1. Engagement Team Independence */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Engagement Team Independence</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Independence workspace summary (Planning phase)</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {engagementTeamLoading ? (
              <div className="flex items-center justify-center h-24">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.totalPeople}</p>
                  <p className="text-xs text-white/60">Total people</p>
                </div>
                <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.declaredIndependence}</p>
                  <p className="text-xs text-white/60">Declared independence</p>
                </div>
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.yetToDeclare}</p>
                  <p className="text-xs text-white/60">Yet to declare</p>
                </div>
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.addedNotDeclared}</p>
                  <p className="text-xs text-white/60">Added but not declared</p>
                </div>
                <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-3">
                  <p className="text-xl font-bold text-white">{independenceSummary.declaredNotAdded}</p>
                  <p className="text-xs text-white/60">Declared but not added</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Materiality Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Materiality Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Planning and performance materiality summary</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {materialityLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold w-48">Materiality Type</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-40">Entity</TableHead>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableHead key={`abcotd-${idx}`} className="text-white/80 font-semibold text-center w-40">
                          <div className="leading-tight">
                            <div className="text-xs italic text-white/60">(ABCOTD)</div>
                            <div>{abcotd.name || `ABCOTD ${idx + 1}`}</div>
                          </div>
                        </TableHead>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableHead key={`component-${idx}`} className="text-white/80 font-semibold text-center w-40">
                          <div className="leading-tight">
                            <div className="text-xs italic text-white/60">(Component)</div>
                            <div>{comp.name || `Component ${idx + 1}`}</div>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Planning Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.planningMateriality)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-pm-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(abcotd.planningMateriality)}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-pm-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.planningMateriality)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Revised Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {materialityTableData.entity.revisedMateriality !== null 
                          ? formatCurrency(materialityTableData.entity.revisedMateriality)
                          : "-"}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-rm-${idx}`} className="text-white/90 text-center">
                          {abcotd.revisedMateriality}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-rm-${idx}`} className="text-white/90 text-center">
                          {comp.revisedMateriality}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">Performance Materiality</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.performanceMateriality)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-perf-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(abcotd.performanceMateriality)}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-perf-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.performanceMateriality)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow className="border-white/5">
                      <TableCell className="text-white/90 font-medium">CTT</TableCell>
                      <TableCell className="text-white/90 text-center tabular-nums">
                        {formatCurrency(materialityTableData.entity.ctt)}
                      </TableCell>
                      {materialityTableData.abcotds.map((abcotd, idx) => (
                        <TableCell key={`abcotd-ctt-${idx}`} className="text-white/90 text-center">
                          {abcotd.ctt}
                        </TableCell>
                      ))}
                      {materialityTableData.components.map((comp, idx) => (
                        <TableCell key={`comp-ctt-${idx}`} className="text-white/90 text-center tabular-nums">
                          {formatCurrency(comp.ctt)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Scoping Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center">
                <Activity className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Scoping Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">ABCOTD Analysis Summary</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {scopingLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
                  <p className="text-2xl font-bold text-white mb-1">{scopingSummary.exceedsMateriality}</p>
                  <p className="text-xs text-white/60">ABCOTDs Exceeding Materiality</p>
                </div>
                <div className="p-4 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20">
                  <p className="text-2xl font-bold text-white mb-1">{scopingSummary.consideredSignificant}</p>
                  <p className="text-xs text-white/60">Considered Significant</p>
                </div>
                <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20">
                  <p className="text-2xl font-bold text-white mb-1">{scopingSummary.qualitativelyMaterial}</p>
                  <p className="text-xs text-white/60">Qualitatively Material (Exceeding)</p>
                </div>
                <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
                  <p className="text-2xl font-bold text-white mb-1">{scopingSummary.qualitativelyMaterialNotExceeding}</p>
                  <p className="text-xs text-white/60">Qualitatively Material (Not Exceeding)</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Significant Risks Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Significant Risks Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Risks assessed as significant</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {risksLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : significantRisks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Risk ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Control(s)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Substantive Procedures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {significantRisks.map((risk) => (
                      <tr
                        key={risk.rommId}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-3 px-4 text-white font-medium">{risk.rommId}</td>
                        <td className="py-3 px-4 text-white/80">{risk.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">{risk.description || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.controlIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.controlIds.map((controlId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {controlId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.procedureIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.procedureIds.map((procedureId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {procedureId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No significant risks found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Deficiency Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500/30 to-orange-600/20 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Deficiency Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Deficiencies Identified</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {deficienciesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : deficiencies.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Deficiency ID</TableHead>
                      <TableHead className="text-white/80 font-semibold">Deficiency Type</TableHead>
                      <TableHead className="text-white/80 font-semibold">Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deficiencies.map((def) => (
                      <TableRow key={def.id} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white">{def.id}</TableCell>
                        <TableCell className="text-white/80">{def.deficiencyType || "N/A"}</TableCell>
                        <TableCell className="text-white/70">{def.description || "N/A"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center text-white/40 text-sm">
                No deficiencies found
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6. Misstatement Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Misstatement Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Identified Misstatements</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {misstatementsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : misstatements.length > 0 ? (
              <div className="space-y-4">
                <div className="overflow-x-auto mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Misstatement ID</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-white/70">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {misstatements.map((mis) => (
                        <tr
                          key={mis.id}
                          className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                          onClick={() => {
                            if (setActiveSection) {
                              setActiveSection("misstatement-library");
                            }
                          }}
                        >
                          <td className="py-3 px-4 text-white">{mis.id}</td>
                          <td className="py-3 px-4 text-white/80">{mis.type}</td>
                          <td className="py-3 px-4 text-white/70">{mis.workspace || "N/A"}</td>
                          <td className="py-3 px-4 text-right text-white">{formatCurrency(mis.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {misstatements.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-white/60 mb-1">Total Misstatement Amount</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(totalMisstatementAmount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 mb-1">Planning Materiality</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(materialityData.planningMateriality)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 mb-1">Performance Materiality</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(materialityData.performanceMateriality)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60 mb-1">Clearly Trivial Threshold</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(materialityData.clearlyTrivialThreshold)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No misstatements found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 7. External Confirmations Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500/30 to-indigo-600/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">External Confirmations Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Domain testing and confirmations overview</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {externalConfirmationsLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Domain Testing</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-white/60">Total records</p>
                        <p className="text-xl font-semibold text-white">{externalConfirmationsSummary.domainTesting.total}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Active</p>
                        <p className="text-xl font-semibold text-emerald-400">{externalConfirmationsSummary.domainTesting.active}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Not Found</p>
                        <p className="text-xl font-semibold text-rose-400">{externalConfirmationsSummary.domainTesting.notFound}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">General Domain</p>
                        <p className="text-xl font-semibold text-amber-400">{externalConfirmationsSummary.domainTesting.generalDomain}</p>
                      </div>
                    </div>
                    {externalConfirmationsSummary.domainTesting.others > 0 && (
                      <p className="text-xs text-white/60 mt-3">
                        Other status records: <span className="text-white">{externalConfirmationsSummary.domainTesting.others}</span>
                      </p>
                    )}
                  </div>

                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Confirmations</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-white/60">Total confirmations</p>
                        <p className="text-xl font-semibold text-white">{externalConfirmationsSummary.confirmations.total}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Received</p>
                        <p className="text-xl font-semibold text-emerald-400">{externalConfirmationsSummary.confirmations.received}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Not received</p>
                        <p className="text-xl font-semibold text-rose-400">{externalConfirmationsSummary.confirmations.notReceived}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/60">Alt testing linked</p>
                        <p className="text-xl font-semibold text-indigo-400">{externalConfirmationsSummary.confirmations.withAlternateTesting}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="border-white/20 text-white/80">
                        Workspaces: {externalConfirmationsSummary.confirmations.uniqueWorkspaces}
                      </Badge>
                      <Badge variant="outline" className="border-white/20 text-white/80">
                        Sample Sets: {externalConfirmationsSummary.confirmations.uniqueSampleSets}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60 mb-3 uppercase tracking-wide">Workspace-wise Confirmation Status</p>
                  {externalConfirmationsSummary.workspaceBreakdown.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-white/80 font-medium">Workspace</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Total</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Received</TableHead>
                            <TableHead className="text-white/80 font-medium text-right">Not Received</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {externalConfirmationsSummary.workspaceBreakdown.map((row) => (
                            <TableRow key={row.workspace} className="border-white/10">
                              <TableCell className="text-white/90">{row.workspace}</TableCell>
                              <TableCell className="text-white/90 text-right">{row.total}</TableCell>
                              <TableCell className="text-emerald-400 text-right">{row.received}</TableCell>
                              <TableCell className="text-rose-400 text-right">{row.notReceived}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-white/50 text-sm">No confirmation entries found.</div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 8. Summary of Management Estimates */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Management Estimates</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Estimates from Review of Estimates for Management Bias</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {estimatesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : rembEstimatesTable.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Name of Estimate</TableHead>
                      <TableHead className="text-white/80 font-semibold">ABCOTD</TableHead>
                      <TableHead className="text-white/80 font-semibold">Estimation Uncertainty</TableHead>
                      <TableHead className="text-white/80 font-semibold">Whether Management has used an expert</TableHead>
                      <TableHead className="text-white/80 font-semibold">Includes fair valuation?</TableHead>
                      <TableHead className="text-white/80 font-semibold">Whether auditor's specialist is involved?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rembEstimatesTable.map((row, index) => (
                      <TableRow key={index} className="border-white/5 hover:bg-white/5">
                        <TableCell className="text-white/90">{row.col_0 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_1 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_2 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_3 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_4 || "-"}</TableCell>
                        <TableCell className="text-white/90">{row.col_5 || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-white/40 text-center py-8">No estimates data available</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
